import { call, put, select } from 'redux-saga/effects';

import type { SagaIterator } from '@redux-saga/types';

import { CheckAvailabilitySuccessPayload } from '../actions';
import {
  cancelReplace,
  completeReplace,
  ReplaceEntries,
} from '../actions/entries';
import {
  selectBets,
  selectDeletesQueue,
  selectInsertsQueue,
} from '../selectors/entries';
import { EntriesBets, EntriesDeletes, EntriesInserts } from '../types/entries';
import checkAvailability from './checkAvailability';

// ---

export default function* replaceEntriesFlow(payload: ReplaceEntries['payload']): SagaIterator {
  const deletes: EntriesDeletes = yield select(selectDeletesQueue);
  const entries: EntriesBets = yield select(selectBets);

  for (let i = 0; i < payload.entries.length; i++) {
    const entryId = payload.entries[i].betId;

    if (!deletes.has(entryId)) { // this actually shouldn't happen
      throw new Error(`replaceEntriesFlow -> the deletion of #${entryId} is not queued`);
    }

    if (!entries.has(entryId)) { // this actually shouldn't happen
      throw new Error(`replaceEntriesFlow -> there's no bet slip entry #${entryId}`);
    }
  }

  // ---

  const entryId = payload.by.bet.entity.id;
  const inserts: EntriesInserts = yield select(selectInsertsQueue);

  if (!inserts.has(entryId)) { // this actually shouldn't happen
    throw new Error(`replaceEntriesFlow -> the insertion of #${entryId} is not queued`);
  }

  if (entries.has(entryId)) { // this actually shouldn't happen
    throw new Error(`replaceEntriesFlow -> the bet slip entry #${entryId} is already in`);
  }

  // -- requesting stake types availability -- //

  let availabilityResult: CheckAvailabilitySuccessPayload;

  const nextEntries = payload.entries.reduce((entries, entry) => {
    return entries.delete(entry.betId); // excluding the removing bet entries,
  }, entries).set(entryId, payload.by.bet); // ... and adding the new bet entry

  try {
    availabilityResult = yield call(checkAvailability, nextEntries);
  }
  catch (err) {
    console.error('replaceEntriesFlow -> check availability request failed', err);
    // Make a note that removing enqueued entries for remove is not a good idea,
    // it causes the situation when you will have wrong state of `availableStakeTypes`.
    // Therefore we cancel entries replacement here:
    yield put(cancelReplace(payload.entries.map((entry) => entry.betId), entryId));
    return; // ... then exit the "replace entries" flow,
    // ... preventing conflicts detection/resolution and recalculation request flows.
  }

  const {
    singles,
    express,
    systems,
    availableStakeTypes,
  } = availabilityResult;

  // no conflicts or resolved w/o cancelling

  yield put(completeReplace({
    ...payload,
    singles,
    express,
    systems,
    availableStakeTypes,
  }));
}
